import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  DndContext, 
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { KanbanColumn } from '../components/kanban/KanbanColumn';
import { KanbanCard } from '../components/kanban/KanbanCard';
import { CardDetailModal } from '../components/kanban/CardDetailModal';
import { ArrowLeft, Plus, Settings, Users, MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

export const KanbanBoardPage = () => {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCard, setActiveCard] = useState(null);
  const [activeColumn, setActiveColumn] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  
  // Column creation
  const [isCreateColumnOpen, setIsCreateColumnOpen] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newColumnColor, setNewColumnColor] = useState('#6366F1');
  
  const columnColors = ['#6366F1', '#F59E0B', '#22C55E', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899'];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchProject();
  }, [projectId, token]);

  const fetchProject = async () => {
    if (!token || !projectId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProject(data);
        setColumns(data.columns || []);
      } else if (response.status === 404) {
        toast.error('Project not found');
        navigate('/projects');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const activeType = active.data.current?.type;
    
    if (activeType === 'card') {
      setActiveCard(active.data.current.card);
    } else if (activeType === 'column') {
      setActiveColumn(active.data.current.column);
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeType = active.data.current?.type;
    const overType = over.data.current?.type;

    if (activeType !== 'card') return;

    // Find the columns
    const activeColumnId = active.data.current.columnId;
    let overColumnId = overType === 'column' ? overId : over.data.current?.columnId;

    if (!overColumnId || activeColumnId === overColumnId) return;

    setColumns(prevColumns => {
      const activeColIndex = prevColumns.findIndex(col => col.id === activeColumnId);
      const overColIndex = prevColumns.findIndex(col => col.id === overColumnId);

      if (activeColIndex === -1 || overColIndex === -1) return prevColumns;

      const newColumns = [...prevColumns];
      const activeCards = [...newColumns[activeColIndex].cards];
      const overCards = [...newColumns[overColIndex].cards];

      const activeCardIndex = activeCards.findIndex(card => card.id === activeId);
      if (activeCardIndex === -1) return prevColumns;

      const [movedCard] = activeCards.splice(activeCardIndex, 1);
      movedCard.column_id = overColumnId;

      // Find insertion index
      let newIndex = 0;
      if (overType === 'card') {
        newIndex = overCards.findIndex(card => card.id === overId);
      }

      overCards.splice(newIndex, 0, movedCard);

      newColumns[activeColIndex] = { ...newColumns[activeColIndex], cards: activeCards };
      newColumns[overColIndex] = { ...newColumns[overColIndex], cards: overCards };

      return newColumns;
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveCard(null);
    setActiveColumn(null);

    if (!over) return;

    const activeType = active.data.current?.type;
    const overType = over.data.current?.type;

    if (activeType === 'column' && overType === 'column') {
      // Column reordering
      const oldIndex = columns.findIndex(col => col.id === active.id);
      const newIndex = columns.findIndex(col => col.id === over.id);

      if (oldIndex !== newIndex) {
        const newColumns = arrayMove(columns, oldIndex, newIndex);
        setColumns(newColumns);

        // Update positions on server
        try {
          await fetch(`${BACKEND_URL}/api/projects/${projectId}/columns/${active.id}/move`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ position: newIndex })
          });
        } catch (error) {
          console.error('Error moving column:', error);
          fetchProject(); // Revert on error
        }
      }
    } else if (activeType === 'card') {
      // Card was moved - update server
      const activeCard = active.data.current.card;
      const newColumnId = activeCard.column_id;
      
      // Find new position
      const columnCards = columns.find(col => col.id === newColumnId)?.cards || [];
      const newPosition = columnCards.findIndex(card => card.id === activeCard.id);

      try {
        await fetch(`${BACKEND_URL}/api/projects/${projectId}/cards/${activeCard.id}/move`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ 
            column_id: newColumnId, 
            position: newPosition >= 0 ? newPosition : 0 
          })
        });
      } catch (error) {
        console.error('Error moving card:', error);
        fetchProject(); // Revert on error
      }
    }
  };

  const handleCreateColumn = async () => {
    if (!newColumnTitle.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/columns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newColumnTitle,
          color: newColumnColor
        })
      });

      if (response.ok) {
        const column = await response.json();
        setColumns(prev => [...prev, column]);
        setIsCreateColumnOpen(false);
        setNewColumnTitle('');
        setNewColumnColor('#6366F1');
        toast.success('Column created');
      }
    } catch (error) {
      console.error('Error creating column:', error);
      toast.error('Failed to create column');
    }
  };

  const handleDeleteColumn = async (columnId) => {
    if (!window.confirm('Delete this column and all its cards?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/columns/${columnId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setColumns(prev => prev.filter(col => col.id !== columnId));
        toast.success('Column deleted');
      }
    } catch (error) {
      console.error('Error deleting column:', error);
      toast.error('Failed to delete column');
    }
  };

  const handleCreateCard = async (columnId, title) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/cards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title,
          column_id: columnId
        })
      });

      if (response.ok) {
        const card = await response.json();
        setColumns(prev => prev.map(col => 
          col.id === columnId 
            ? { ...col, cards: [...col.cards, card] }
            : col
        ));
        toast.success('Card created');
      }
    } catch (error) {
      console.error('Error creating card:', error);
      toast.error('Failed to create card');
    }
  };

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setIsCardModalOpen(true);
  };

  const handleCardUpdate = async (cardId, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/cards/${cardId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedCard = await response.json();
        setColumns(prev => prev.map(col => ({
          ...col,
          cards: col.cards.map(card => 
            card.id === cardId ? updatedCard : card
          )
        })));
        setSelectedCard(updatedCard);
        toast.success('Card updated');
      }
    } catch (error) {
      console.error('Error updating card:', error);
      toast.error('Failed to update card');
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/cards/${cardId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setColumns(prev => prev.map(col => ({
          ...col,
          cards: col.cards.filter(card => card.id !== cardId)
        })));
        setIsCardModalOpen(false);
        setSelectedCard(null);
        toast.success('Card deleted');
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error('Failed to delete card');
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#1a1d21]">
        <div className="text-gray-400">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#1a1d21]">
        <div className="text-gray-400">Project not found</div>
      </div>
    );
  }

  return (
    <div 
      className="flex-1 flex flex-col min-h-screen"
      style={{ backgroundColor: project.background || '#1a1d21' }}
      data-testid="kanban-board-page"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft size={18} className="mr-1" />
            Projects
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-white">{project.name}</h1>
            {project.description && (
              <p className="text-sm text-gray-400">{project.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Members */}
          <div className="flex -space-x-2 mr-4">
            {project.members?.slice(0, 4).map((member) => (
              <div
                key={member.user_id}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium border-2 border-[#1a1d21]"
                style={{ backgroundColor: member.avatar_color }}
                title={member.name}
              >
                {member.name?.charAt(0).toUpperCase()}
              </div>
            ))}
          </div>
          
          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
            <Users size={18} className="mr-1" />
            Share
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <MoreHorizontal size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1a1d21] border-white/20">
              <DropdownMenuItem className="text-gray-300 hover:text-white focus:bg-white/10">
                <Settings size={14} className="mr-2" />
                Project Settings
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 h-full">
            <SortableContext
              items={columns.map(col => col.id)}
              strategy={horizontalListSortingStrategy}
            >
              {columns.map(column => (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  cards={column.cards || []}
                  onCreateCard={handleCreateCard}
                  onDeleteColumn={handleDeleteColumn}
                  onCardClick={handleCardClick}
                  projectLabels={project.labels || []}
                />
              ))}
            </SortableContext>

            {/* Add Column Button */}
            <button
              onClick={() => setIsCreateColumnOpen(true)}
              className="flex-shrink-0 w-72 h-12 bg-white/5 hover:bg-white/10 border-2 border-dashed border-white/20 rounded-xl flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-all"
              data-testid="add-column-btn"
            >
              <Plus size={20} />
              Add Column
            </button>
          </div>

          <DragOverlay>
            {activeCard && (
              <KanbanCard
                card={activeCard}
                isDragging
                projectLabels={project.labels || []}
              />
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Create Column Dialog */}
      <Dialog open={isCreateColumnOpen} onOpenChange={setIsCreateColumnOpen}>
        <DialogContent className="bg-[#0F2942] border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Add Column</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Column Title</Label>
              <Input
                value={newColumnTitle}
                onChange={(e) => setNewColumnTitle(e.target.value)}
                placeholder="e.g. In Review"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                data-testid="column-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Color</Label>
              <div className="flex gap-2">
                {columnColors.map(color => (
                  <button
                    key={color}
                    onClick={() => setNewColumnColor(color)}
                    className={`w-8 h-8 rounded-full transition-all ${
                      newColumnColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0F2942]' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setIsCreateColumnOpen(false)}
              className="text-gray-400 hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateColumn}
              disabled={!newColumnTitle.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="create-column-submit"
            >
              Add Column
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal
          isOpen={isCardModalOpen}
          onClose={() => {
            setIsCardModalOpen(false);
            setSelectedCard(null);
          }}
          card={selectedCard}
          projectLabels={project.labels || []}
          projectMembers={project.members || []}
          onUpdate={handleCardUpdate}
          onDelete={handleDeleteCard}
          projectId={projectId}
          token={token}
        />
      )}
    </div>
  );
};

export default KanbanBoardPage;
